 
import { registerView } from '../viewRegistry';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import { getCanonicalSafeArea } from '../safeArea';
import { CRT_PROFILES } from '../crt/crtProfiles.js';
import { generateAsciiArt, ensureFigletFont } from '../../utils/asciiArt';

// Text to display
const DISPLAY_TEXT = 'DCW';

// Pre-generated FIGlet (ANSI Shadow, width 60) so we never wait during the
// very first frame (screenshots rely on this).
const PREGENERATED_ART = `██████╗  ██████╗██╗    ██╗\n` +
`██╔══██╗██╔════╝██║    ██║\n` +
`██║  ██║██║     ██║ █╗ ██║\n` +
`██║  ██║██║     ██║███╗██║\n` +
`██████╔╝╚██████╗╚███╔███╔╝\n` +
`╚═════╝  ╚═════╝ ╚══╝╚══╝ `;

let asciiArtCache = PREGENERATED_ART; // immediate default
let asciiArtLoaded = true; // we have something to render right away

async function generateASCIICache() {
  if (asciiArtCache && asciiArtCache !== PREGENERATED_ART) return asciiArtCache;
  try {
    // Ensure the FIGlet font is loaded first (only once)
    await ensureFigletFont('ANSI Shadow', '/fonts/figlet/ANSI_Shadow.flf');
    // Use ANSI Shadow font with increased width for better visibility
    asciiArtCache = await generateAsciiArt(DISPLAY_TEXT, { font: 'ANSI Shadow', width: 60 });
    asciiArtLoaded = true;
    console.log('ASCII art generated successfully for icon:', asciiArtCache);
    return asciiArtCache;
  } catch (err) {
    console.error('Failed to generate ASCII art:', err);
    return null;
  }
}

// Generate ASCII art immediately on module load
generateASCIICache();

// (Auto‐redirect removed – now honours whatever ?profile=… the user provides)

function renderIconDCW(ctx, t, w, h, params = {}) {
  // Use the renderer-provided dimensions directly so the ASCII art scales to
  // fill 100 % of the screenshot canvas.
  const bw = w;
  const bh = h;

  // Clear the canvas with black
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, bw, bh);
  
  // Always render pre-generated FIGlet ASCII art; bitmap renderer is NOT used
  // here because the build requires a consistent, stylised FIGlet look.
  
  // If we have ASCII art but it's empty, show an error
  if (!asciiArtCache || asciiArtCache.trim() === '') {
    ctx.fillStyle = '#FF3939';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 24px monospace';
    ctx.fillText('ASCII Art Error', bw / 2, bh / 2);
    return;
  }
  
  // Draw the ASCII art
  const asciiLines = asciiArtCache.split('\n');

  // --- Robust width estimation ------------------------------------------------
  // In some headless Chrome builds, ctx.measureText for box-drawing glyphs (█ ║ ╔ etc.)
  // returns zero which broke our previous scaling (ASCII art positioned off-screen).
  // We therefore fall back to a simple character-count heuristic when the measured
  // width seems suspicious (≤ 10 px for entire string).

  ctx.font = '113px monospace';
  const longestLine = asciiLines.reduce((a, b) => (a.length > b.length ? a : b));

  // Empirical average monospace glyph width ≈ 0.6 × fontSize (113 px → 67.8 px)
  const measured = ctx.measureText(longestLine).width;
  const heuristic = longestLine.length * 113 * 0.6;
  const widthAt100 = measured > 10 ? measured : heuristic;

  // Leave an 8 % horizontal margin so the glow never touches the edges
  const H_MARGIN_RATIO = 0.16;
  const usableWidth = bw * (1 - H_MARGIN_RATIO);

  const candidateByWidth = (usableWidth / widthAt100) * 100;
  const candidateByHeight = bh / asciiLines.length;

  // Choose the smaller candidate and shave off 5 % for glow safety
  const lineHeight = Math.max(1, Math.round(Math.min(candidateByWidth, candidateByHeight) * 0.95));

  ctx.font = `${lineHeight}px monospace`;
  
  // Center the ASCII art vertically
  const startY = (bh - lineHeight * asciiLines.length) / 2;
  
  // Apply glow effect
  ctx.fillStyle = '#39FF14'; // Bright green phosphor color
  ctx.shadowColor = '#39FF14';
  ctx.shadowBlur = 10; // Increased glow
  
  // Draw each line of ASCII art
  for (let i = 0; i < asciiLines.length; i++) {
    const line = asciiLines[i];
    const lineWidth = ctx.measureText(line).width;
    const startX = (bw - lineWidth) / 2;
    
    // For icon generation we always use plain monospace drawing. The canonical
    // bitmap renderer may load a few frames later causing the logo to pop
    // out—unwanted in static asset generation.
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(line, startX, startY + i * lineHeight);
  }
  
  // Reset shadow
  ctx.shadowBlur = 0;
  
  // Signal completion for build scripts (only after successful render)
  if (typeof window !== 'undefined' && !window.DCW_ICON_READY) {
    window.DCW_ICON_READY = true;
    console.log('[ICON_DCW] Icon rendered successfully');
  }
}

registerView('icon_dcw', renderIconDCW); 